import { ReactElement } from "react";
import HeroSection from "@components/severalbrands/HeroSection";
import MetaTags from "@components/severalbrands/metaTags";
import { getFinalLogosForLogoSLider } from "@components/severalbrands/utils";
import { getKeyWordsForMetaTags } from "@components/severalbrands/utils";
import { useDomainContext } from "@hooks/useDomainContext";
import LogoSlider from "@components/severalbrands/logoSlider";
import { DomainInfo } from "@components/severalbrands/utils/services";

// Main general category shared components
import GeneralCategorySecondSection from "@components/severalbrands/b2b/secondSection";
import GeneralCategoryThirdSection from "@components/severalbrands/b2b/thirdSection";

// specific category components
import ForthSectionContainer from "@components/severalbrands/b2b/forthSectionContainer";

// images and icons
import B2BLeadCultivationIcon from "@components/severalbrands/b2b/secondSection/strategicLeadGeneration/icons/b2bLeadCultivationIcon";
import CostPerCallIcon from "@components/severalbrands/howWeWorkPage/partneringWithSeveralBrands/icons/costPerCallIcon";
import CostPerClickIcon from "@components/severalbrands/howWeWorkPage/partneringWithSeveralBrands/icons/costPerClickIcon";
import CostPerLeadIcon from "@components/severalbrands/howWeWorkPage/partneringWithSeveralBrands/icons/costPerLeadIcon";
import CostPerRetainerIcon from "@components/severalbrands/howWeWorkPage/partneringWithSeveralBrands/icons/costPerRetainerIcon";

// category data
const imgTextComponentContent = {
    img: "/severalbrands/assets/images/torts.webp",
    mainTitle: "Get Results with Performance-Based Lead Generation",
    paragraphs: [
        "We go beyond SEM, SEO, and digital ads, layering in content marketing, social media strategy, and email campaigns tailored to your business. Our performance-based model means you only pay for the results we deliver, no leads, no cost, so your investment ties directly to outcomes you can measure. Pricing is built around what fits your goals, whether that's leads, calls, or sales, and with fast onboarding, you can typically start receiving leads within 48 to 72 hours of signing.",
    ],
    showAnimation: true,
    showButton: true,
    buttonTitle: "Schedule a Call",
};
const strategicLeadGenerationContent = {
    title: "Strategic Lead Generation: Timing Meets Precision",
    topDescription:
        "Our approach to B2B lead generation is built on identifying genuine buyer intent and connecting it to the right sales opportunity.",
    bottomDescription:
        "Outsourcing your lead generation is a cost-effective way to grow without stretching your internal team thin. Partnering with us means you can focus on your product and your clients, while we handle sourcing and delivering leads that fit your criteria.",
    list: [
        {
            title: "B2B Lead Cultivation",
            tabName: "B2B",
            slug: "b2b",
            description:
                "We focus on identifying and cultivating genuine B2B buying intent, so the leads we deliver are engaged and ready for your team to follow up on, meaning fewer wasted conversations and more opportunities that convert. We've worked across a wide range of industries, helping companies that serve other businesses grow their pipeline.",
            icon: <B2BLeadCultivationIcon />,
        },
    ],
};
const howWeHelpContent = {
    mainTitle: "How We Help Your Business Attract More Customers",
    list: [
        {
            title: "Cost Per Click (CPC)",
            p: "Ideal for building exposure and testing new audiences. You only pay when someone clicks, so you can gauge interest before committing further.",
            icon: <CostPerClickIcon />,
        },
        {
            title: "Cost Per Lead (CPL)",
            p: "Our primary model for B2B. You pay only when a prospect takes a measurable action, like submitting a form, so your spend follows real buyer intent.",
            icon: <CostPerLeadIcon />,
        },
        {
            title: "Cost Per Call",
            p: "Built for businesses where phone conversations drive revenue. You pay only for calls that meet an agreed duration or result in a real conversation.",
            icon: <CostPerCallIcon />,
        },
        {
            title: "Custom & Blended Models",
            p: "Every business is different. If CPC, CPL, or cost-per-call doesn't fit your goals exactly, we'll build a model around what matters most to your pipeline.",
            icon: <CostPerRetainerIcon />,
        },
    ],
    benefitListClassName: "lg:max-w-[1500px] lg:mx-auto",
    benefitItemClassName: "lg:!w-[480px] 3xl:!w-[580px]",
};
const howWeWorkContent = {
    title: "How We Work",
    list: [
        {
            description:
                "We work with your business to build a pricing strategy that aligns with your performance goals and requirements.",
        },
        {
            description:
                "We research your ideal customer profile (ICP) and build targeted campaigns designed to reach real prospects and maximize conversions.",
        },
        {
            description:
                "Every lead goes through our verification system, which filters out fraud, checks syntax, and flags duplicates, as well as a human review, before it's sent to you. We use keyword optimization and strategic timing throughout to support stronger conversion rates.",
        },
        {
            description:
                "You share regular performance reports on lead contact status and conversion outcomes, which helps us refine targeting and improve lead quality over time.",
        },
        {
            description:
                "Together, we agree on KPIs for cost per sale or opportunity, and adjust payouts based on performance against those benchmarks.",
        },
        {
            description:
                "We repeat steps 2 through 4, refining continuously, until we reach an autopilot mode that consistently meets our shared KPIs.",
        },
    ],
};

export default function B2bGeneralContainer(): ReactElement {
    const { domain, category } = useDomainContext();

    const finalLogos = getFinalLogosForLogoSLider(
        domain.config?.sliderLogos,
        category?.slug,
    );
    const description = category?.metatags?.find(
        (meta) => meta.key === "description",
    )?.value;
    return (
        <>
            <MetaTags
                title={
                    category?.title ??
                    "B2B Lead Generation Services | SeveralBrands.com"
                }
                description={
                    description ??
                    "See how our B2B lead generation strategies can strengthen your sales funnel. Schedule a call today."
                }
                keyWords={getKeyWordsForMetaTags(
                    domain as unknown as DomainInfo,
                )}
            />
            <HeroSection />
            {finalLogos?.length > 0 && (
                <LogoSlider
                    logos={finalLogos}
                    mainTitle="We Collaborate With Hundreds of Companies in Different Sectors"
                />
            )}
            <GeneralCategorySecondSection
                mainData={{
                    imgTextComponentContent: imgTextComponentContent,
                    strategicLeadGenerationContent:
                        strategicLeadGenerationContent,
                }}
            />
            <GeneralCategoryThirdSection
                mainData={{
                    howWeHelpContent: howWeHelpContent,
                    howWeWorkContent: howWeWorkContent,
                }}
            />
            <ForthSectionContainer />
        </>
    );
}
